import * as _ from 'lodash';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  Invoice as InvoiceTypes,
  Util,
} from '@finley-technologies/finley-types';

import {
  InvoiceCalculationComparisonFragmentGQLType,
  InvoiceComponentWithInvoiceItemsInputGQLType,
  InvoiceItemGQLType,
  InvoiceSummaryFragmentGQLType,
  TAccountGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { truncateUuid } from 'packages/common-frontend/utils';
import { ChartDateRange } from 'packages/loanProduct/ui/receivablesDashboard/utils';

// Base type for invoice list items - contains the essential fields needed for list views
// This is compatible with both InvoiceListItemFragmentGQLType and InvoiceSummaryFragmentGQLType
export type InvoiceListItemBase = Pick<
  InvoiceSummaryFragmentGQLType,
  | 'id'
  | 'creditFacilityId'
  | 'status'
  | 'totalAmount'
  | 'dueDate'
  | 'targetInvoiceDate'
  | 'paymentDate'
  | 'created_at'
  | 'invoiceComponentsWithInvoiceItems'
  | 'creditFacility'
>;

// more detailed status derived from Invoice.status, due date, and target invoice date
export type InvoiceComputedStatus =
  | InvoiceTypes.InvoiceStatus
  | 'scheduled' // status === created && !!targetInvoiceDate && dueDate > currentDate + 3
  | 'dueSoon' // (status === created || status === sent) && dueDate <= currentDate + 3
  | 'overdue'; // dueDate < currentDate

export const highlightComputedStatuses: InvoiceComputedStatus[] = [
  'dueSoon',
  'overdue',
  'paid',
];
export type HighlightComputedStatus = typeof highlightComputedStatuses[number];

export type InvoiceWithComputedStatus = InvoiceListItemBase & {
  computedStatus: InvoiceComputedStatus;
};

export type AccrualsByTAccountMap = {
  [tAccountId: string]: {
    tAccount: TAccountGQLType;
    projectedAccruedAmount: number;
    actualAccruedAmount: number;
    totalAccruedAmount: number;
  };
};

export const getInvoiceComputedStatus = ({
  status,
  dueDate,
  targetInvoiceDate,
}: {
  status: InvoiceTypes.InvoiceStatus;
  dueDate: Util.FullDateStr;
  targetInvoiceDate?: Util.FullDateStr;
}): InvoiceComputedStatus => {
  switch (status) {
    case 'canceled':
      return 'canceled';
    case 'paid':
      return 'paid';
    case 'created':
      if (dueDate < commonDate.currentDateStr()) {
        return 'overdue';
      }
      if (dueDate < commonDate.addDays(commonDate.currentDateStr(), 3)) {
        return 'dueSoon';
      }
      if (targetInvoiceDate) {
        return 'scheduled';
      }
      return 'created';
    default:
      return finleyAssertUnreachable(status, 'Invoice status is not supported');
  }
};

export const getInvoicesWithComputedStatus = ({
  invoices,
}: {
  invoices?: InvoiceListItemBase[];
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  return _.compact(
    invoices.map((invoice) => {
      const { status, dueDate, targetInvoiceDate } = invoice;
      const computedStatus = getInvoiceComputedStatus({
        status,
        dueDate,
        targetInvoiceDate: targetInvoiceDate ?? undefined,
      });

      return {
        ...invoice,
        computedStatus,
      };
    }),
  );
};

export const filterInvoicesForDatesAndCreditFacilities = ({
  invoices,
  dateRange,
  selectedCreditFacilityIds,
}: {
  invoices?: InvoiceWithComputedStatus[];
  dateRange: ChartDateRange | null;
  selectedCreditFacilityIds: string[];
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  return invoices.filter((invoice) => {
    const invoiceDueDateJs = commonDate.fullDateStrToDate(invoice.dueDate);
    const includesCreditFacility = selectedCreditFacilityIds.includes(
      invoice.creditFacilityId,
    );

    if (!dateRange) {
      return includesCreditFacility;
    }

    return (
      invoiceDueDateJs >= dateRange.chartDataStartDate &&
      invoiceDueDateJs <= dateRange.chartDataEndDate &&
      includesCreditFacility
    );
  });
};

export const filterInvoicesForSelectedComputedStatus = ({
  invoices,
  selectedComputedStatus,
}: {
  invoices?: InvoiceWithComputedStatus[];
  selectedComputedStatus: HighlightComputedStatus | null;
}): InvoiceWithComputedStatus[] | undefined => {
  if (!invoices) {
    return undefined;
  }

  if (selectedComputedStatus) {
    return invoices.filter(
      (invoice) => invoice.computedStatus === selectedComputedStatus,
    );
  }

  return invoices;
};

export const getInvoiceOverviewInitialDateRange = (): ChartDateRange => {
  const currentDate = new Date();
  const initialStartDate = new Date();
  const initialEndDate = new Date();

  initialStartDate.setDate(currentDate.getDate() - 90);
  initialEndDate.setDate(currentDate.getDate() + 90);

  return {
    chartDataStartDate: initialStartDate,
    chartDataEndDate: initialEndDate,
  };
};

export const getBankAccountDisplay = ({
  institutionName,
  accountName,
  accountNumber,
}: {
  institutionName: string;
  accountName: string;
  accountNumber: string;
}) => {
  return `${institutionName}, ${accountName} (...${accountNumber.substring(
    accountNumber.length - 4,
  )})`;
};

export const getInterestInvoiceItemLabel = ({
  invoiceItem,
  invoiceLoanId,
}: {
  invoiceItem:
    | Omit<
        InvoiceItemGQLType,
        'id' | 'invoiceId' | 'created_at' | 'updated_at' | 'accountingAccount'
      >
    | InvoiceItemGQLType;
  invoiceLoanId?: string | null;
}) => {
  if (!invoiceItem.capitalizedLoanId) {
    return 'Cash';
  }

  if (invoiceLoanId === invoiceItem.capitalizedLoanId) {
    return 'Interest Capitalization to This Loan';
  }

  return `Interest Capitalization to Loan ${truncateUuid(
    invoiceItem.capitalizedLoanId,
  )}`;
};

export const getUpdatedInvoiceTotal = ({
  invoiceComponentsWithInvoiceItems,
  updatedInvoiceComponentTotal,
  invoiceComponentIndex,
}: {
  invoiceComponentsWithInvoiceItems: InvoiceComponentWithInvoiceItemsInputGQLType[];
  updatedInvoiceComponentTotal: number;
  invoiceComponentIndex: number;
}) => {
  const newInvoiceTotal = invoiceComponentsWithInvoiceItems.reduce<number>(
    (acc, invoiceComponentWithInvoiceItems, idx) => {
      if (idx === invoiceComponentIndex) {
        return acc + updatedInvoiceComponentTotal;
      }
      return (
        acc + invoiceComponentWithInvoiceItems.invoiceComponent.totalAmount
      );
    },
    0,
  );

  return commonUtils.jsRound({
    value: newInvoiceTotal,
    precision: 2,
  });
};

export const getCapitalizedLoanLabel = ({
  loanName,
  loanId,
  externalLoanId,
  selectedLoanId,
}: {
  loanName: string;
  loanId: string;
  externalLoanId?: string | null;
  selectedLoanId?: string | null;
}) => {
  if (loanId === selectedLoanId) {
    return 'This Loan';
  }

  if (loanName !== '') {
    return loanName;
  }

  return `${truncateUuid(loanId)} ${
    externalLoanId ? `(${externalLoanId})` : ''
  }`;
};

export const getLoanLabel = (params: {
  loanId: string;
  loanName: string;
  externalLoanId: string | null;
  alwaysIncludeLoanName?: boolean;
}): string => {
  const {
    loanName,
    loanId,
    externalLoanId,
    alwaysIncludeLoanName = false,
  } = params;

  if (externalLoanId) {
    if (alwaysIncludeLoanName) {
      return `${loanName} (${externalLoanId})`;
    }
    return externalLoanId;
  }

  return loanName ?? commonUtils.truncateUuid(loanId);
};

export const getInvoiceType = (invoice: InvoiceListItemBase) => {
  const invoiceItemTypes = new Set<
    'interest' | 'interestCapitalization' | 'principal' | 'fee'
  >();

  invoice.invoiceComponentsWithInvoiceItems.forEach(
    ({ invoiceComponent, invoiceItems }) => {
      if (invoiceComponent.invoiceComponentType === 'creditFacility') {
        invoiceItemTypes.add('fee');
      } else {
        invoiceItems.forEach(({ accountingAccount, capitalizedLoanId }) => {
          if (accountingAccount.tAccount === 'accruedInterest') {
            if (capitalizedLoanId) {
              invoiceItemTypes.add('interestCapitalization');
            } else {
              invoiceItemTypes.add('interest');
            }
          } else if (
            accountingAccount.tAccount === 'longTermDebt' ||
            accountingAccount.tAccount === 'adminAgentPrincipalPayable'
          ) {
            invoiceItemTypes.add('principal');
          }
        });
      }
    },
  );

  // if there's both cash and capitalized interest, just use the "interest" label to represent both to save room
  if (
    invoiceItemTypes.has('interestCapitalization') &&
    invoiceItemTypes.has('interest')
  ) {
    invoiceItemTypes.delete('interestCapitalization');
  }

  return Array.from(invoiceItemTypes)
    .map((type) => commonUtils.toTitleCase(type))
    .join(', ');
};

export const invoiceHasCalculationMismatches = (
  invoiceCalculationComparisons?: InvoiceCalculationComparisonFragmentGQLType[],
) => {
  if (
    !invoiceCalculationComparisons ||
    invoiceCalculationComparisons.length === 0
  ) {
    return false;
  }
  return invoiceCalculationComparisons.some(
    (item) =>
      item.invoiceComponentDifferences.length > 0 ||
      item.invoiceDisbursementItemDifferences.principal.length > 0 ||
      item.invoiceDisbursementItemDifferences.interest.length > 0,
  );
};
